import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpResponse } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { TransferState, makeStateKey, StateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { I18nService } from '../i18n/i18n.service';
import { UrlAddressService } from '../url-address/url-address.service';

@Injectable()
export class StateInterceptor implements HttpInterceptor {
    private transferState = inject(TransferState);
    private _i18 = inject(I18nService);
    private platformId = inject(PLATFORM_ID);
    private urlAddressService = inject(UrlAddressService);

    intercept(request: HttpRequest<any>, next: HttpHandler) {
        if (request.method !== 'GET') {
            return next.handle(request);
        }

        // Header and footer shouldn't be cached anymore since it removes at outer server.ts level

        // In the server we add the Url due to different address, but we do not need it on the client
        const key: StateKey<string> = makeStateKey<string>(
            request.urlWithParams.replace(/^.*\/\/[^\/]+/, '') +
                //this fixes issue with multiple objects from diff langs
                // If there are paramas already we add &lang=heb/eng otherwise ?lang=heb/eng
                (request.url.includes('?') ? '&lang=' : '?lang=') +
                this._i18.getLang(),
        );

        if (isPlatformServer(this.platformId)) {
            return next.handle(request).pipe(
                tap((event) => {
                    if (!(event instanceof HttpResponse)) {
                        return;
                    }

                    // We don't want to cache the category tree since it's over 100 kb and doesn't make the page flicks
                    //if(request.url.includes(CategoryService.sCategoryTreeUrl)) { return; }

                    // Skipping the whole esi_angular areas from request caching
                    if (this.urlAddressService.getURL().includes('/esi_angular?')) {
                        return;
                    }

                    // TODO: Refactor these to Global  ( For now it cause unknown exeption)
                    if (request.url.toLowerCase().includes('contentscategory/tree')) {
                        return;
                    }
                    if (request.url.toLowerCase().endsWith('rabbi')) {
                        return;
                    }
                    if (request.url.toLowerCase().endsWith('rabbis')) {
                        return; // Heavey over 250 kb
                    }
                    if (request.url.toLowerCase().includes('/api/times/alldailytimes')) {
                        return; // Not nessarily due to hide state anyways
                    }
                    if (request.url.toLowerCase().includes('article/api/contents/paging_data')) {
                        return; //temporarily no state until fixed
                    }
                    if (request.url.toLowerCase().startsWith('/api/reply')) {
                        return; //no state for reply because anyways the replies section will be ready before the user will scroll to it
                    }
                    if (request.url.toLowerCase().includes('api/test/categoriestree')) {
                        return; // Heavey
                    }

                    //don't save 'current' api in all pages
                    let stateSave = request.headers.has('stateSave');
                    if (!stateSave && request.url.toLowerCase().endsWith('current')) {
                        return;
                    }

                    // updateCategoryFromDictionery call category's parent and then call to it's kids and call circular depencies.
                    if (request.url.includes('contentscategory?cat')) {
                        this.transferState.set(key, this.createJsonSafeBody(event.body));
                        return;
                    }
                    this.transferState.set(key, this.createJsonSafeBody(event.body));
                    //todo: raziel fix
                    //this.transferState.set(key, (<HttpResponse<any>> event).body);
                }),
            );
        } else {
            const storedResponse = this.transferState.get<any>(key, null);
            if (storedResponse) {
                const response = new HttpResponse({
                    body: storedResponse,
                    status: 200,
                });
                //remove for refresh the data in the next request (use the caching service to maintain data for small preiod of time)
                this.transferState.remove(key);
                return of(response);
            } else {
                return next.handle(request);
            }
        }
    }

    private createJsonSafeBody<T>(body: T): T {
        const ancestors = new WeakSet<object>();

        const walk = (value: unknown): unknown => {
            if (typeof value !== 'object' || value === null) {
                return value;
            }
            if (ancestors.has(value)) {
                return undefined;
            }
            ancestors.add(value);

            let result: unknown;
            if (Array.isArray(value)) {
                result = value.map(walk);
            } else {
                const out: Record<string, unknown> = {};
                for (const key in value) {
                    if (Object.prototype.hasOwnProperty.call(value, key)) {
                        out[key] = walk((value as Record<string, unknown>)[key]);
                    }
                }
                result = out;
            }

            ancestors.delete(value);
            return result;
        };

        return walk(body) as T;
    }
}
